import React, { useState, useRef, useEffect } from "react";

/**
 * Tooltip — pure React, no external dependency.
 * target prop accepts a ref or element id string.
 * placement: top | bottom | left | right
 */
const Tooltip = ({ target, placement = "top", open: controlledOpen, toggle, children }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const visible = isControlled ? controlledOpen : internalOpen;

  const targetEl = useRef(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    // Resolve target element by id string (with or without leading #) or ref
    if (typeof target === "string") {
      const id = target.startsWith("#") ? target.slice(1) : target;
      targetEl.current = document.getElementById(id);
    } else if (target && target.current) {
      targetEl.current = target.current;
    }

    const el = targetEl.current;
    if (!el) return;

    const show = () => isControlled ? toggle?.() : setInternalOpen(true);
    const hide = () => isControlled ? toggle?.() : setInternalOpen(false);

    el.addEventListener("mouseenter", show);
    el.addEventListener("mouseleave", hide);
    el.addEventListener("focus", show);
    el.addEventListener("blur", hide);

    return () => {
      el.removeEventListener("mouseenter", show);
      el.removeEventListener("mouseleave", hide);
      el.removeEventListener("focus", show);
      el.removeEventListener("blur", hide);
    };
  }, [target, isControlled, toggle]);

  if (!visible) return null;

  return (
    <div
      ref={tooltipRef}
      role="tooltip"
      className={`tooltip bs-tooltip-${placement} show`}
      style={{ position: "absolute", zIndex: 1070 }}
    >
      <div className="arrow" />
      <div className="tooltip-inner">{children}</div>
    </div>
  );
};

export default Tooltip;
